import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import {
  requestPresign,
  uploadFileToR2,
  deleteAttachments,
  extractKeys,
} from '../services/chatUpload';
import './AvatarEditPopup.css';

/*
  프로필 썸네일 편집 팝업.
  - 파일 선택(이미지만) → square 미리보기 영역에 줌/팬 조정
  - 원형 마스크로 어디가 썸네일이 될지 시각화
  - 저장: 캔버스로 정사각형(512x512) 크롭 → R2 업로드 → user_profiles 업데이트 → reload
  - 삭제: R2 객체 삭제 + url null + avatar_cleared_at = now() → reload
*/

const VIEWPORT = 280; // 미리보기 캔버스 한 변
const OUTPUT_SIZE = 512; // 업로드용 출력 한 변

function getAvatarKeyFromUrl(url) {
  if (typeof url !== 'string') return null;
  // r2 public base url (https://<domain>/<key>) 또는 그냥 key 형태
  try {
    const u = new URL(url);
    // path 가 /lansly/users/... 로 시작하면 leading slash 만 빼고 key
    const path = u.pathname.replace(/^\/+/, '');
    if (path.startsWith('lansly/')) return path;
    return null;
  } catch {
    if (url.startsWith('lansly/')) return url;
    return null;
  }
}

export default function AvatarEditPopup({
  user,
  currentAvatarUrl,
  displayName,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation('avatarEdit');
  const [sourceImage, setSourceImage] = useState(null); // HTMLImageElement
  const [sourceObjectUrl, setSourceObjectUrl] = useState(null);
  const [sourceFile, setSourceFile] = useState(null); // File (사용자가 새로 고른 파일)
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // '썸네일 삭제' 누르면 true → 미리보기에 닉네임 첫 글자만 노출, '저장' 눌러야 실제 삭제.
  const [pendingClear, setPendingClear] = useState(false);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  // 새 파일을 골랐을 때만 편집(드래그/줌) 가능. 기존 썸네일만 보여주거나 삭제 대기 상태에선 잠금.
  const isEditable = !!sourceFile && !pendingClear;
  // 저장 활성: 새 파일을 골랐거나 / 삭제 대기 상태일 때.
  const canSave = !!sourceFile || pendingClear;

  // object URL 정리
  useEffect(() => {
    return () => {
      if (sourceObjectUrl) URL.revokeObjectURL(sourceObjectUrl);
    };
  }, [sourceObjectUrl]);

  // 팝업 마운트 시 기존 썸네일 url 이 있으면 미리보기로 로드 (편집 잠금 상태).
  // 사용자가 "이미지 선택" 으로 새 파일 고르기 전까진 저장 비활성.
  // 1차로 crossOrigin: 'anonymous' 시도 (R2 가 CORS 헤더를 보내주면 canvas 에 그릴 수 있음).
  // 실패하면 crossOrigin 없이 한 번 더 시도 — 미리보기 표시만 되면 충분.
  useEffect(() => {
    if (!currentAvatarUrl) return;
    let cancelled = false;
    const applyIfFirst = (img) => {
      if (cancelled) return;
      setSourceImage((prev) => (prev ? prev : img));
    };
    const loadWithoutCors = () => {
      const img = new Image();
      img.onload = () => applyIfFirst(img);
      img.onerror = () => {};
      img.src = currentAvatarUrl;
    };
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => applyIfFirst(img);
    img.onerror = loadWithoutCors;
    img.src = currentAvatarUrl;
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 새 이미지 들어오면 viewport 에 fit 되게 zoom/offset 초기화
  useEffect(() => {
    if (!sourceImage) return;
    const w = sourceImage.naturalWidth;
    const h = sourceImage.naturalHeight;
    if (!w || !h) return;
    // 짧은 변을 viewport 에 맞추는 기본 줌 (cover 효과)
    const minScale = VIEWPORT / Math.min(w, h);
    setZoom(minScale);
    setOffset({ x: 0, y: 0 });
  }, [sourceImage]);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('errorImageOnly'));
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError(t('errorTooLarge', { n: 20 }));
      return;
    }
    setError('');
    // 삭제 대기 상태에서 새 파일 고르면 자동으로 해제 (의도 변경)
    setPendingClear(false);
    if (sourceObjectUrl) URL.revokeObjectURL(sourceObjectUrl);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setSourceImage(img);
    };
    img.onerror = () => {
      setError(t('errorLoadFailed'));
    };
    img.src = url;
    setSourceObjectUrl(url);
    setSourceFile(file);
  };

  // 캔버스에 그리기 — viewport 기준 source 이미지를 zoom/offset 으로 배치
  const drawPreview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sourceImage) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, VIEWPORT, VIEWPORT);
    const w = sourceImage.naturalWidth * zoom;
    const h = sourceImage.naturalHeight * zoom;
    const x = VIEWPORT / 2 - w / 2 + offset.x;
    const y = VIEWPORT / 2 - h / 2 + offset.y;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(sourceImage, x, y, w, h);
  }, [sourceImage, zoom, offset]);

  useEffect(() => {
    drawPreview();
  }, [drawPreview]);

  // 드래그 팬 — 새 파일을 고른 상태일 때만 활성
  const onPointerDown = (e) => {
    if (!sourceImage || !isEditable) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origin: { ...offset },
    };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset({
      x: dragRef.current.origin.x + dx,
      y: dragRef.current.origin.y + dy,
    });
  };
  const onPointerUp = (e) => {
    if (dragRef.current) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {}
    }
    dragRef.current = null;
  };

  // 휠 줌 — 새 파일을 고른 상태일 때만 활성
  const onWheel = (e) => {
    if (!sourceImage || !isEditable) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    setZoom((z) => clamp(z + delta * z, minZoomFor(sourceImage), 6));
  };

  const onCropSave = async () => {
    if (!user?.id || busy || !canSave) return;
    setBusy(true);
    setError('');
    try {
      // 분기 1 — 삭제 대기 상태였으면 url/avatar_cleared_at 만 정리하고 R2 객체 삭제
      if (pendingClear) {
        const oldKey = getAvatarKeyFromUrl(currentAvatarUrl);
        const { error: updErr } = await supabase
          .from('user_profiles')
          .update({
            updated_avatar_url: null,
            avatar_cleared_at: new Date(),
            updated_at: new Date(),
          })
          .eq('id', user.id);
        if (updErr) throw updErr;
        if (oldKey) deleteAttachments([oldKey]);
        onSaved?.();
        return;
      }
      // 분기 2 — 새 파일을 골라 크롭한 경우 (기존 업로드 흐름)
      if (!sourceImage || !sourceFile) {
        throw new Error(t('errorNoImage'));
      }
      const out = document.createElement('canvas');
      out.width = OUTPUT_SIZE;
      out.height = OUTPUT_SIZE;
      const octx = out.getContext('2d');
      octx.imageSmoothingEnabled = true;
      octx.imageSmoothingQuality = 'high';
      const w = sourceImage.naturalWidth * zoom;
      const h = sourceImage.naturalHeight * zoom;
      const x = VIEWPORT / 2 - w / 2 + offset.x;
      const y = VIEWPORT / 2 - h / 2 + offset.y;
      const scale = OUTPUT_SIZE / VIEWPORT;
      octx.drawImage(sourceImage, x * scale, y * scale, w * scale, h * scale);
      const blob = await new Promise((resolve, reject) => {
        out.toBlob(
          (b) => (b ? resolve(b) : reject(new Error(t('errorConvertFailed')))),
          'image/jpeg',
          0.9
        );
      });
      const fileName = `avatar-${Date.now()}.jpg`;
      const wrappedFile = new File([blob], fileName, { type: 'image/jpeg' });
      const presign = await requestPresign({
        kind: 'avatar',
        files: [{ name: wrappedFile.name, type: wrappedFile.type, size: wrappedFile.size }],
      });
      const res = presign?.files?.[0];
      if (!res?.ok) {
        throw new Error(res?.error || t('errorPresignFailed'));
      }
      await uploadFileToR2({ file: wrappedFile, url: res.url, type: res.type }).promise;
      const publicBase = (process.env.REACT_APP_R2_PUBLIC_BASE_URL || '').replace(/\/+$/, '');
      const publicUrl = publicBase ? `${publicBase}/${res.key}` : res.key;
      const { error: updErr } = await supabase
        .from('user_profiles')
        .update({
          updated_avatar_url: publicUrl,
          avatar_cleared_at: null,
          updated_at: new Date(),
        })
        .eq('id', user.id);
      if (updErr) throw updErr;
      const oldKey = getAvatarKeyFromUrl(currentAvatarUrl);
      if (oldKey && oldKey !== res.key) {
        deleteAttachments([oldKey]);
      }
      onSaved?.();
    } catch (e) {
      console.error('아바타 저장 에러:', e);
      setError(e?.message || t('errorSaveFailed'));
      setBusy(false);
    }
  };

  // '썸네일 삭제' 클릭 — 즉시 db 반영하지 않고 미리보기만 닉네임 첫 글자로 바꿈.
  // 실제 삭제는 사용자가 '저장' 을 눌러야 처리됨.
  // sourceImage/sourceFile 은 건드리지 않음 → '삭제 취소' 누르면 이전 미리보기 자동 복귀.
  const onMarkForDelete = () => {
    if (busy) return;
    setError('');
    setPendingClear(true);
  };

  // pendingClear 해제 — '다른 이미지 선택' 누르면 자동으로 해제됨 (onPickFile 에서)
  const onCancelClear = () => {
    setPendingClear(false);
    setError('');
  };


  const hasImage = !!sourceImage;

  return (
    <div className="avatar-edit-overlay" onClick={() => !busy && onClose?.()}>
      <div className="avatar-edit-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="avatar-edit-close"
          onClick={() => !busy && onClose?.()}
          aria-label={t('close')}
        >
          ×
        </button>
        <h2 className="avatar-edit-title">{t('title')}</h2>
        <div className="avatar-edit-stage">
          <div
            className={`avatar-edit-canvas-wrap${isEditable ? '' : ' is-locked'}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            style={{ width: VIEWPORT, height: VIEWPORT }}
          >
            <canvas
              ref={canvasRef}
              width={VIEWPORT}
              height={VIEWPORT}
              className="avatar-edit-canvas"
              style={{ display: pendingClear || !hasImage ? 'none' : 'block' }}
            />
            {pendingClear || !hasImage ? (
              <div className="avatar-edit-initial">
                <span>{deriveAvatarInitial(displayName) || '?'}</span>
              </div>
            ) : (
              <div className="avatar-edit-mask" aria-hidden />
            )}
          </div>
          {(() => {
            const sliderMin = hasImage ? minZoomFor(sourceImage) : 0.1;
            // 잠금 상태(편집 불가)에선 시각적으로 슬라이더를 항상 맨 왼쪽(min) 에 둠 — 살짝 떠있는 느낌 방지
            const sliderValue = isEditable ? zoom : sliderMin;
            return (
              <input
                type="range"
                min={sliderMin}
                max="4"
                step="0.01"
                value={sliderValue}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="avatar-edit-zoom"
                aria-label={t('zoom')}
                disabled={!isEditable}
              />
            );
          })()}
          <p className="avatar-edit-hint">
            {isEditable ? t('hintEditable') : t('hintLocked')}
          </p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onPickFile}
          className="avatar-edit-file-input"
        />

        {error && <div className="avatar-edit-error">{error}</div>}

        <div className="avatar-edit-actions">
          <button
            type="button"
            className="avatar-edit-btn-ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            {hasImage ? t('pickAnother') : t('pickImage')}
          </button>
          {currentAvatarUrl && !pendingClear && (
            <button
              type="button"
              className="avatar-edit-btn-danger"
              onClick={onMarkForDelete}
              disabled={busy}
            >
              {t('deleteThumbnail')}
            </button>
          )}
          {pendingClear && (
            <button
              type="button"
              className="avatar-edit-btn-ghost"
              onClick={onCancelClear}
              disabled={busy}
            >
              {t('cancelDelete')}
            </button>
          )}
          <div className="avatar-edit-actions-right">
            <button
              type="button"
              className="avatar-edit-btn-outline"
              onClick={() => !busy && onClose?.()}
              disabled={busy}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="avatar-edit-btn-primary"
              onClick={onCropSave}
              disabled={busy || !canSave}
            >
              {busy ? t('saving') : t('save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function clamp(v, min, max) {
  return Math.min(Math.max(v, min), max);
}

function minZoomFor(img) {
  if (!img?.naturalWidth || !img?.naturalHeight) return 0.1;
  return VIEWPORT / Math.min(img.naturalWidth, img.naturalHeight);
}

// 외부 ref 용 — currentAvatarUrl 에서 R2 key 추출 (필요 시 export)
export { getAvatarKeyFromUrl };

// 닉네임 첫 글자 추출 — 한글 1글자, 영어/숫자는 첫 letter 대문자.
export function deriveAvatarInitial(name) {
  if (typeof name !== 'string') return '?';
  const s = name.trim();
  if (!s) return '?';
  // 첫 코드 포인트 단위로 자름 (이모지/한자 포함)
  const first = Array.from(s)[0];
  if (!first) return '?';
  // ASCII 라틴이면 uppercase 1글자
  if (/^[a-zA-Z]$/.test(first)) return first.toUpperCase();
  return first;
}

// extractKeys 는 services/chatUpload.js 에서 가져와 사용
// (avatar 삭제 시 cleanup 만 하면 되므로 별도 처리는 안 함)
void extractKeys;
